import React, { FC, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import cuid from 'cuid';
import { uniqBy } from 'lodash';

import { LinkContainer, ListItem, ListItemBottom, SvgIcon } from '@portal/common/components';
import { ResourceList } from '@portal/common/types';

import _ from '../../locale';
import { MY_FAVORITES_PATH } from '../../router/paths';
import { GROUP_CONTENT_LIST, MY_FAVORITES_LIST } from '../../store/resource-lists/models';
import {
    getCustomLists,
    getDefaultList,
    getPublicLists,
} from '../../store/resource-lists/selectors';
import { addDashboardTab, updateDashboardTab } from '../../store/user-settings/actions';

const RightArrowIcon = (props) => (
    <SvgIcon
        src="/icons/ic-arrow.svg"
        size="medium"
        color={(theme) => theme.color.gray40}
        style={{ transform: 'scaleX(-1)' }}
    />
);

type Props = {
    toggleVisibility: () => void;
    tabListId?: string;
    tabIndex?: number;
};

const DropdownWithLists: FC<Props> = ({ toggleVisibility, tabListId, tabIndex }) => {
    const dispatch = useDispatch();
    const customLists = useSelector(getCustomLists); // @todo Rename to myLists (lists created by me + subscribe)
    const publicLists = useSelector(getPublicLists);
    const defaultList = useSelector(getDefaultList);

    const addTab = useCallback(
        (list: ResourceList) => {
            dispatch(
                addDashboardTab({
                    ...list,
                    tabId: cuid(),
                })
            );
            toggleVisibility();
        },
        [toggleVisibility]
    );

    const updateTab = useCallback(
        (list: ResourceList) => {
            dispatch(
                updateDashboardTab({
                    index: tabIndex,
                    list: { ...list, tabId: cuid() },
                })
            );
            toggleVisibility();
        },
        [toggleVisibility, tabIndex]
    );

    const handleClick = useCallback(
        (list: ResourceList) => {
            isNaN(tabIndex) ? addTab(list) : updateTab(list);
        },
        [addTab, updateTab, tabIndex]
    );

    const lists = uniqBy(
        [
            MY_FAVORITES_LIST,
            defaultList,
            ...(customLists || []),
            ...(publicLists || []),
            GROUP_CONTENT_LIST,
        ],
        'id'
    ) as ResourceList[];

    return lists
        .map((list) => (
            <ListItem
                key={list.id}
                onClick={handleClick.bind(null, list)}
                isSelected={list.id === tabListId}
            >
                {list.name}
            </ListItem>
        ))
        .concat(
            <LinkContainer key={'manage-lists'} to={MY_FAVORITES_PATH}>
                <ListItemBottom customIcon={<RightArrowIcon />}>
                    {_.get('lists_manage_lists')}
                </ListItemBottom>
            </LinkContainer>
        );
};

export default DropdownWithLists;
